import React from "react"
import PropTypes from "prop-types"
import { NeoBadge, NeoCard, NeoAlert } from "../../../components/Common"
import { useEvaluationFormLogic } from "../Hooks/useEvaluationFormLogic"
import { MemberSearch } from "../../../components/Common"
import TechnicalEvaluationForm from "./TechnicalEvaluationForm"
import TestEvaluationForm from "./TestEvaluationForm"

const EvaluationCard = ({ schedule, activeMode = "technical" }) => {
  const internalActiveTab = activeMode === "technical" ? "1" : "2"

  const {
    color,
    activityName,
    employeeName,
    startTime,
    endTime,
    isEvaluationDisabled,
  } = schedule || {}

  const evaluationLogic = useEvaluationFormLogic({ classId: schedule?.idClass, activeMode })

  const {
    formReady,
    searchText,
    setSearchText,
    activeEvent,
    activeTestEvent,
    addCandidates,
    showNoAutocompleteResults,
    handleAddMember,
  } = evaluationLogic

  return (
    <NeoCard
      thinBorder={true}
      className="h-100 shadow-sm overflow-hidden d-flex flex-column animate__animated animate__fadeIn"
      bodyClassName="p-0 d-flex flex-column h-100"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {schedule ? (
        <div className="bg-white px-4 py-4 border-bottom-0 shadow-sm position-relative z-1 flex-shrink-0">
          {color && <div className="position-absolute top-0 start-0 w-100" style={{ height: '4px', backgroundColor: color }}></div>}

          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <NeoBadge color={activeMode === 'technical' ? 'primary' : 'info'} className="px-2 py-1 fw-bold font-size-10">
                    {activeMode === 'technical' ? 'AVALIAÇÃO TÉCNICA' : 'TESTES DE PERFORMANCE'}
                  </NeoBadge>
                </div>
                <h3 className="mb-2 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>{activityName || "Evento"}</h3>
                <div className="d-flex align-items-center gap-3">
                  {employeeName && (
                    <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13">
                      <i className="mdi mdi-account-circle-outline fs-5" />
                      <span>{employeeName}</span>
                    </div>
                  )}
                  <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13 bg-light px-2 py-1 rounded">
                    <i className="mdi mdi-clock-outline text-primary" />
                    <span>{startTime} — {endTime}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-100 mt-2">
              <MemberSearch
                value={searchText}
                onChange={setSearchText}
                disabled={!formReady || isEvaluationDisabled}
                candidates={addCandidates}
                onSelect={handleAddMember}
                showNoResults={showNoAutocompleteResults}
                noResultsLabel="Nenhum membro encontrado com contrato ativo para adicionar."
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white px-4 py-4 border-bottom-0 shadow-sm position-relative z-1 flex-shrink-0">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <NeoBadge color={activeMode === 'technical' ? 'primary' : 'info'} className="px-2 py-1 fw-bold font-size-10">
                    {activeMode === 'technical' ? 'AVALIAÇÃO TÉCNICA' : 'TESTES DE PERFORMANCE'}
                  </NeoBadge>
                </div>
                <h3 className="mb-2 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>Painel de {activeMode === 'technical' ? 'Avaliação' : 'Testes'}</h3>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13 bg-light px-2 py-1 rounded">
                    <i className="mdi mdi-information-outline text-muted" />
                    <span>Nenhuma turma selecionada</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-100 mt-2">
              <MemberSearch
                value={searchText}
                onChange={setSearchText}
                disabled={true}
                candidates={[]}
                onSelect={() => { }}
                showNoResults={false}
                noResultsLabel="Nenhum membro"
              />
            </div>
          </div>
        </div>
      )}

      <div className="p-0 d-flex flex-column flex-grow-1" style={{ overflow: 'hidden' }}>
        <div className="h-100 overflow-auto bg-white" style={{ minHeight: '400px' }}>
          {!schedule ? (
            <div className="p-4 p-md-5 d-flex flex-column h-100">
              {/* Global Active Event Info (Shows when no class is selected) */}
              {activeMode === "performance" ? (
                activeTestEvent ? (
                  <NeoAlert
                    color="success"
                    icon={(['fixed_time', 'distance'].includes(activeTestEvent?.testType)) ? 'mdi-timer-sand' : 'mdi-run-fast'}
                    title={activeTestEvent.name}
                    style={{ borderLeftColor: '#0ab39c' }}
                  >
                    <div className="mb-1">
                      <NeoBadge color="info" className="fw-bold px-2 py-1 font-size-10">Teste de Performance Ativo</NeoBadge>
                    </div>
                    <div className="mt-1">
                      <strong className="text-dark">Regra: </strong>
                      {(['fixed_time', 'distance'].includes(activeTestEvent?.testType))
                        ? `Tempo Fixo (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Distância do Aluno`
                        : `Distância Fixa (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Tempo do Aluno`
                      }
                    </div>
                  </NeoAlert>
                ) : (
                  <NeoAlert
                    color="warning"
                    title="Nenhum Teste Agendado"
                  >
                    Não há provas ou testes de performance com prazos ativos no momento.
                  </NeoAlert>
                )
              ) : (
                activeEvent ? (
                  <NeoAlert
                    color="primary"
                    icon="mdi-star-circle-outline"
                    title={activeEvent.name}
                  >
                    <div className="mb-1">
                      <NeoBadge color="primary" className="fw-bold px-2 py-1 font-size-10">Avaliação Técnica Ativa</NeoBadge>
                    </div>
                    <div className="mt-1">
                      Período de graduações e exames de faixa em andamento.
                    </div>
                  </NeoAlert>
                ) : (
                  <NeoAlert
                    color="warning"
                    title="Nenhuma Avaliação Agendada"
                  >
                    Não existem ciclos de avaliação técnica com prazos ativos no momento.
                  </NeoAlert>
                )
              )}

              <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center mt-5">
                <div className={`rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-4 ${activeMode === 'performance' ? 'text-info' : 'text-primary'}`} style={{ width: 80, height: 80 }}>
                  <i className={activeMode === 'performance' ? "mdi mdi-timer-sand" : "mdi mdi-text-box-search-outline"} style={{ fontSize: '40px' }} />
                </div>
                <h4 className="text-dark fw-bold mb-2">Painel de {activeMode === 'performance' ? 'Testes' : 'Avaliação'}</h4>
                <p className="text-muted font-size-15" style={{ maxWidth: '300px' }}>Selecione uma turma na lista lateral para visualizar e avaliar os alunos.</p>
              </div>
            </div>
          ) : (
            <div className="p-3 p-md-4">
              {internalActiveTab === "1" && (
                <TechnicalEvaluationForm
                  key={`tech_${schedule.idClass}_${schedule.idActivity}_${schedule.startTime}`}
                  classId={schedule.idClass}
                  idActivity={schedule.idActivity}
                  classTeacherId={schedule.idEmployee}
                  classEmployeeName={schedule.employeeName || ""}
                  activityName={schedule.activityName || ""}
                  evaluationLogic={evaluationLogic}
                />
              )}

              {internalActiveTab === "2" && (
                <TestEvaluationForm
                  key={`test_${schedule.idClass}_${schedule.idActivity}_${schedule.startTime}`}
                  idActivity={schedule.idActivity}
                  evaluationLogic={evaluationLogic}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </NeoCard>
  )
}

EvaluationCard.propTypes = {
  schedule: PropTypes.shape({
    activityName: PropTypes.string,
    employeeName: PropTypes.string,
    areaName: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    maxCapacity: PropTypes.number,
    enrolledCount: PropTypes.number,
    attendanceRecorded: PropTypes.bool,
    presentCount: PropTypes.number,
    absentCount: PropTypes.number,
    color: PropTypes.string,
    idClass: PropTypes.string,
    idActivity: PropTypes.string,
  })
}

export default EvaluationCard
